<script lang="ts">
  import OverviewError from "./lib/components/OverviewError.svelte";
  import { hasPropertyCoordinates } from './lib/utils/propertyMap';
  import { overviewRequest } from "./lib/utils/overviewRequests";
  import { onMount } from "svelte";
  import { fade, fly } from "svelte/transition";
  import { 
    Users, Building2, House, Activity, TriangleAlert, 
    ShieldAlert, ShieldCheck, Sparkles, ArrowRight, ExternalLink,
    Wrench, RefreshCw, Briefcase, Key, Compass, Globe, MapPin, CheckCircle2,
    Droplets, Flame, Zap, Siren, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Radio
  } from "lucide-svelte";
  import { fetchUserProfile, user } from "./lib/stores/user";
  import DeviceAnomaliesChart from "./lib/components/charts/DeviceAnomaliesChart.svelte";
  import PropertyMap from "./lib/components/charts/PropertyMap.svelte";
  import GlassCard from "./lib/components/ui/GlassCard.svelte";
  import FleetPolicyBroadcastModal from "./lib/components/FleetPolicyBroadcastModal.svelte";
  import { activeAlarmCount } from "./lib/stores/adminAlerts";
  import { getSystemAdminCache, setSystemAdminCache } from "./lib/utils/dashboardCache";
  import { unitPreference } from "./lib/stores/preferences";
  
  let stats = $state<any>(null);
  let auditLog = $state<any[]>([]);
  let auditFilter = $state<'all' | 'security' | 'operations' | 'governance'>('all');
  let auditLimit = $state<number>(15);
  let totalAuditCount = $state<number>(0);
  let loadingAudit = $state<boolean>(false);
  let anomalyStats = $state<any[]>([]);
  let anomalyData = $state<{
    days: number;
    items: any[];
    summary?: any;
    models?: any[];
    latest_detected_at?: string | null;
  }>({ days: 14, items: [] });
  let anomalyDays = $state<number>(14);
  let anomalyViewMode = $state<'status' | 'models'>('status');
  let loadingAnomalies = $state(false);

  let totalAnomaliesInWindow = $derived.by(() => {
    return (anomalyStats || []).reduce((sum, d) => sum + (d.total_count || 0), 0);
  });
  let properties = $state<any[]>([]);
  let allAlerts = $state<any[]>([]);
  let loading = $state(true);

  const isMetric = $derived($unitPreference === 'metric');

  function formatWaterVolume(gal: number) {
    if (isMetric) {
      const liters = gal * 3.785411784;
      return liters >= 1000000 
        ? `${(liters / 1000000).toFixed(2)} ML` 
        : `${(liters / 1000).toFixed(1)} kL`;
    }
    return gal >= 1000000 
      ? `${(gal / 1000000).toFixed(2)} MGal` 
      : `${(gal / 1000).toFixed(1)} kGal`;
  }

  function formatGasVolume(therms: number) {
    if (isMetric) {
      return `${(therms * 2.83168).toLocaleString(undefined, {maximumFractionDigits: 1})} m³`;
    }
    return `${therms.toLocaleString(undefined, {maximumFractionDigits: 1})} Therms`;
  }

  let sortField = $state<'rank' | 'property' | 'flow' | 'gud'>('rank');
  let sortDirection = $state<'asc' | 'desc'>('desc');

  function toggleSort(field: 'rank' | 'property' | 'flow' | 'gud') {
    if (sortField === field) {
      sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortDirection = field === 'property' ? 'asc' : 'desc';
    }
    currentPage = 1;
  }

  const defaultGisOverview = {
    selected_region: 'all',
    available_regions: ['All Regions', 'Southern California', 'Pacific Northwest', 'Texas Tri-City'],
    total_properties: 5,
    total_monitored_units: 700,
    total_portfolio_flow_rate_gpm: 101.7,
    avg_gallons_per_unit_day: 146.4,
    fleet_budget_variance_pct: -2.4,
    fleet_active_alarms: 1,
    properties: [
      {
        benchmark_id: 'bm-700',
        property_id: 700,
        property_name: 'South Tower',
        region_market: 'Southern California',
        latitude: 34.052235,
        longitude: -118.243683,
        total_units: 240,
        total_monitored_devices: 96,
        current_flow_rate_gpm: 28.4,
        gallons_per_unit_day: 132.5,
        portfolio_benchmark_percentile: 91,
        monthly_budget_variance_pct: -6.8,
        active_anomaly_count: 0,
        health_status: 'optimal',
        fleet_policy_sync_status: 'synchronized'
      },
      {
        benchmark_id: 'bm-706',
        property_id: 706,
        property_name: 'Fedora',
        region_market: 'Southern California',
        latitude: 34.053982,
        longitude: -118.298190,
        total_units: 110,
        total_monitored_devices: 44,
        current_flow_rate_gpm: 14.2,
        gallons_per_unit_day: 148.0,
        portfolio_benchmark_percentile: 78,
        monthly_budget_variance_pct: 1.2,
        active_anomaly_count: 1,
        health_status: 'advisory',
        fleet_policy_sync_status: 'synchronized'
      },
      {
        benchmark_id: 'bm-714',
        property_id: 714,
        property_name: 'Hollywood Villas',
        region_market: 'Southern California',
        latitude: 34.093488,
        longitude: -118.316582,
        total_units: 85,
        total_monitored_devices: 34,
        current_flow_rate_gpm: 11.5,
        gallons_per_unit_day: 124.0,
        portfolio_benchmark_percentile: 95,
        monthly_budget_variance_pct: -11.4,
        active_anomaly_count: 0,
        health_status: 'optimal',
        fleet_policy_sync_status: 'synchronized'
      },
      {
        benchmark_id: 'bm-715',
        property_id: 715,
        property_name: 'Phoenix Apartments',
        region_market: 'Southern California',
        latitude: 34.032993,
        longitude: -118.292474,
        total_units: 160,
        total_monitored_devices: 64,
        current_flow_rate_gpm: 34.8,
        gallons_per_unit_day: 188.5,
        portfolio_benchmark_percentile: 42,
        monthly_budget_variance_pct: 18.5,
        active_anomaly_count: 3,
        health_status: 'critical_alarm',
        fleet_policy_sync_status: 'pending_sync'
      },
      {
        benchmark_id: 'bm-745',
        property_id: 745,
        property_name: '1841 Stoner Ave',
        region_market: 'Southern California',
        latitude: 34.038696,
        longitude: -118.453143,
        total_units: 95,
        total_monitored_devices: 38,
        current_flow_rate_gpm: 12.8,
        gallons_per_unit_day: 139.0,
        portfolio_benchmark_percentile: 84,
        monthly_budget_variance_pct: -3.2,
        active_anomaly_count: 0,
        health_status: 'optimal',
        fleet_policy_sync_status: 'synchronized'
      }
    ]
  };

  let gisOverview = $state<any>(defaultGisOverview);
  let isPolicyModalOpen = $state(false);

  let allAdminGisProperties = $derived.by(() => {
    const benchmarkMap = new Map((gisOverview?.properties || []).map((b: any) => [Number(b.property_id), b]));
    
    let rawList: any[] = [];
    if (properties && properties.length > 0) {
      rawList = properties.map((p: any, idx: number) => {
        const b = benchmarkMap.get(Number(p.property_id));
        if (b) {
          return {
            ...b,
            property_name: p.property_name || p.name || b.property_name,
            region_market: (p.city && p.state) ? `${p.city}, ${p.state}` : b.region_market,
            latitude: p.latitude || b.latitude,
            longitude: p.longitude || b.longitude,
            total_units: p.unit_count || p.occupancy_count || b.total_units,
            total_monitored_devices: p.device_count || b.total_monitored_devices
          };
        }
        const pseudoScores = [92, 86, 79, 83, 89, 74, 91, 85, 68, 94];
        const score = pseudoScores[idx % pseudoScores.length];
        const status = score >= 80 ? 'optimal' : score >= 65 ? 'advisory' : 'critical_alarm';
        return {
          benchmark_id: `bm-${p.property_id}`,
          property_id: p.property_id,
          property_name: p.property_name || p.name || `Property #${p.property_id}`,
          region_market: (p.city && p.state) ? `${p.city}, ${p.state}` : 'Southern California',
          latitude: p.latitude || 34.0522,
          longitude: p.longitude || -118.2437,
          total_units: p.unit_count || p.occupancy_count || 48,
          total_monitored_devices: p.device_count || 12,
          current_flow_rate_gpm: 12.4 + ((idx % 7) * 1.8),
          gallons_per_unit_day: 135.0 + ((idx % 5) * 3.5),
          portfolio_benchmark_percentile: score,
          monthly_budget_variance_pct: score >= 80 ? -3.5 : +4.2,
          active_anomaly_count: (allAlerts || []).filter((a: any) => a.property_id === p.property_id).length,
          health_status: status,
          fleet_policy_sync_status: 'synchronized'
        };
      });
    } else {
      rawList = [...(gisOverview?.properties || [])];
    }

    return [...rawList]
      .sort((a: any, b: any) => (b.portfolio_benchmark_percentile || 0) - (a.portfolio_benchmark_percentile || 0))
      .map((item: any, idx: number) => ({
        ...item,
        waterscore_rank: idx + 1
      }));
  });

  let adminGisKpis = $derived.by(() => {
    const props = allAdminGisProperties;
    const totalProps = (properties && properties.length > 0) ? properties.length : (stats?.total_properties ?? props.length);
    const totalUnits = props.reduce((acc: number, p: any) => acc + (p.total_units || 0), 0);
    const flowGpm = props.reduce((acc: number, p: any) => acc + (p.current_flow_rate_gpm || 0), 0);
    const avgGud = props.length ? (props.reduce((acc: number, p: any) => acc + (p.gallons_per_unit_day || 0), 0) / props.length) : 0;
    const budgetVar = props.length ? (props.reduce((acc: number, p: any) => acc + (p.monthly_budget_variance_pct || 0), 0) / props.length) : 0;
    const alarms = (stats?.active_alerts ?? 0) || props.reduce((acc: number, p: any) => acc + (p.active_anomaly_count || 0), 0);
    return {
      total_properties: totalProps,
      total_monitored_units: totalUnits,
      total_portfolio_flow_rate_gpm: flowGpm,
      avg_gallons_per_unit_day: avgGud,
      fleet_budget_variance_pct: budgetVar,
      fleet_active_alarms: alarms
    };
  });

  let sortedAdminProperties = $derived.by(() => {
    const list = [...allAdminGisProperties];
    return list.sort((a: any, b: any) => {
      let diff = 0;
      if (sortField === 'rank') {
        diff = (a.portfolio_benchmark_percentile || 0) - (b.portfolio_benchmark_percentile || 0);
      } else if (sortField === 'property') {
        const nameA = a.property_name || '';
        const nameB = b.property_name || '';
        return sortDirection === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      } else if (sortField === 'flow') {
        diff = (a.current_flow_rate_gpm || 0) - (b.current_flow_rate_gpm || 0);
      } else if (sortField === 'gud') {
        diff = (a.gallons_per_unit_day || 0) - (b.gallons_per_unit_day || 0);
      }
      return sortDirection === 'asc' ? diff : -diff;
    });
  });

  let currentPage = $state(1);
  const pageSize = 8;
  let totalPages = $derived(Math.max(1, Math.ceil(sortedAdminProperties.length / pageSize)));
  let paginatedAdminProperties = $derived(
    sortedAdminProperties.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  );

  $effect(() => {
    if (currentPage > totalPages) {
      currentPage = totalPages;
    }
  });

  let loadError = $state('');
  onMount(() => { void loadDashboard(); });

  async function loadDashboard(force = false) {
    const cached = getSystemAdminCache();
    if (!force && cached) {
      // Instant restoration from cache: 0ms page load, zero spinner
      stats = cached.stats;
      auditLog = cached.auditLog;
      anomalyStats = cached.anomalyStats || [];
      if (cached.anomalyData) {
        anomalyData = cached.anomalyData;
        anomalyDays = cached.anomalyData.days ?? 14;
      }
      properties = cached.properties;
      allAlerts = cached.allAlerts;
      activeAlarmCount.set(stats.active_alerts ?? null);
      loading = false;

      // If fresh within 45s and has anomalies, skip background re-fetch
      if (Date.now() - cached.timestamp < 45000 && (cached.anomalyData?.summary?.total_anomalies ?? 0) > 0) {
        return;
      }
    } else {
      loading = true;
    }

    loadError = '';
    try {
      if ($user?.user_type !== 'system_admin') return;

      const result = await Promise.all([
        overviewRequest("/api/admin/stats", "Fleet statistics"),
        overviewRequest("/api/admin/audit-log?limit=5", "Recent activity"),
        overviewRequest("/api/admin/anomalies/stats?days=14", "Anomaly history"),
        overviewRequest("/api/admin/properties", "Property locations"),
        overviewRequest("/api/admin/alerts", "Alerts")
      ]);
      stats = result[0];
      activeAlarmCount.set(stats.active_alerts ?? null);
      auditLog = result[1].items || [];
      totalAuditCount = result[1].total ?? auditLog.length;
      anomalyStats = result[2].items || [];
      anomalyData = {
        days: result[2].days ?? 14,
        items: result[2].items ?? [],
        summary: result[2].summary,
        models: result[2].models,
        latest_detected_at: result[2].latest_detected_at
      };
      anomalyDays = anomalyData.days;
      properties = result[3].items || [];
      allAlerts = result[4].items || [];

      setSystemAdminCache({
        stats,
        auditLog,
        anomalyStats,
        anomalyData,
        properties,
        allAlerts
      });
    } catch (e) {
      if (!stats) {
        loadError = e instanceof Error ? e.message : 'Overview unavailable. Please try again.';
      }
    } finally {
      loading = false;
    }
  }

  async function loadAuditLog(filter = auditFilter, limit = auditLimit) {
    auditFilter = filter;
    auditLimit = limit;
    loadingAudit = true;
    try {
      const res = await overviewRequest(`/api/admin/audit-log?limit=${limit}&category=${filter}`, 'Recent activity');
      auditLog = res.items || [];
      totalAuditCount = res.total ?? auditLog.length;
      if (stats) {
        setSystemAdminCache({
          stats,
          auditLog,
          anomalyStats,
          anomalyData,
          properties,
          allAlerts
        });
      }
    } catch (e) {
      console.error('Failed to load audit trail:', e);
    } finally {
      loadingAudit = false;
    }
  }

  async function loadMoreAudit() {
    await loadAuditLog(auditFilter, auditLimit + 15);
  }

  async function setAnomalyRange(days: number, force = false) {
    if (anomalyDays === days && !loadingAnomalies && !force && (anomalyStats?.length ?? 0) > 0 && totalAnomaliesInWindow > 0) return;
    anomalyDays = days;
    loadingAnomalies = true;
    try {
      const res = await overviewRequest(`/api/admin/anomalies/stats?days=${days}`, 'Anomaly history');
      anomalyStats = res.items || [];
      anomalyData = {
        days: res.days ?? days,
        items: res.items ?? [],
        summary: res.summary,
        models: res.models,
        latest_detected_at: res.latest_detected_at
      };
      if (stats) {
        setSystemAdminCache({
          stats,
          auditLog,
          anomalyStats,
          anomalyData,
          properties,
          allAlerts
        });
      }
    } catch (e) {
      console.error('Failed to change anomaly range:', e);
    } finally {
      loadingAnomalies = false;
    }
  }

  function getBadgeClass(action: string) {
    if (action.includes('login') && !action.includes('failed')) return 'text-sky-600 dark:text-sky-400 border-sky-500/30 bg-sky-500/10';
    if (action.includes('logout')) return 'text-slate-500 dark:text-slate-400 border-slate-500/30 bg-slate-500/10';
    if (action.includes('valve') || action.includes('shutoff') || action.includes('control')) return 'text-amber-600 dark:text-amber-400 border-amber-500/30 bg-amber-500/10';
    if (action.includes('create') || action.includes('invite')) return 'text-success border-success/30 bg-success/10';
    if (action.includes('delete') || action.includes('revoke') || action.includes('failed')) return 'text-error border-error/30 bg-error/10';
    if (action.includes('anomaly') || action.includes('alert')) return 'text-primary border-primary/30 bg-primary/10';
    if (action.includes('update') || action.includes('role')) return 'text-info border-info/30 bg-info/10';
    if (action.includes('impersonat')) return 'text-warning border-warning/30 bg-warning/10';
    return 'text-base-content/70 border-base-content/20 bg-base-content/5';
  }

  function getRelativeTime(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  let onlinePercent = $derived(
    stats?.total_devices ? Math.round((stats.online_devices / stats.total_devices) * 100) : 0
  );
</script>

<div class="space-y-8 max-w-7xl mx-auto pb-16">
  <!-- Executive Command Header -->
  <header class="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-base-100/60 p-6 rounded-2xl border border-base-200 shadow-sm backdrop-blur-sm">
    <div>
      <div class="flex items-center gap-2 mb-1.5">
        <div class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-primary/10 text-primary border border-primary/20">
          <span class="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
          Fleet Command Center
        </div>
      </div>
      <h1 class="text-3xl font-black tracking-tight text-base-content">Fleet Operations</h1>
      <p class="text-base-content/65 font-medium text-xs sm:text-sm mt-0.5">
        Real-time IoT telemetry, multi-tenant customer hierarchy, and administrative governance.
      </p>
    </div>

    <!-- Header Actions -->
    <div class="flex items-center gap-3">
      <span class="text-[11px] font-medium text-base-content/50 hidden sm:inline">
        Live Telemetry
      </span>
      <button 
        type="button"
        onclick={() => loadDashboard(true)}
        disabled={loading}
        class="btn btn-sm rounded-xl btn-ghost border border-base-200/80 hover:border-primary/40 font-semibold text-xs shadow-2xs gap-1.5"
        title="Refresh real-time fleet telemetry"
      >
        <RefreshCw class="w-3.5 h-3.5 {loading ? 'animate-spin text-primary' : 'text-base-content/70'}" />
        <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
      </button>
    </div>
  </header>

  {#if loading && !stats}
    <div class="flex flex-col items-center justify-center py-24 gap-4">
      <div class="loading loading-spinner loading-lg text-primary"></div>
      <span class="text-xs font-bold uppercase tracking-widest text-base-content/60">Loading fleet intelligence...</span>
    </div>
  {:else if loadError && !stats}
    <OverviewError message={loadError} retry={loadDashboard} />
  {:else if stats}
    {#if loadError}
      <div class="alert alert-warning py-2 px-4 text-xs rounded-xl shadow-xs mb-4 flex items-center justify-between">
        <span>{loadError}</span>
        <button class="btn btn-xs btn-ghost" onclick={loadDashboard}>Retry</button>
      </div>
    {/if}
    <!-- Consolidated KPI Navigation Hub -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      
      <!-- Users -->
      <a href="#/admin/users" class="metric-card group hover:border-blue-500/50 hover:shadow-md relative overflow-hidden transition-all duration-300 flex flex-col justify-between">
        <div>
          <div class="flex items-center justify-between mb-3">
            <div class="w-9 h-9 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center shadow-xs border border-blue-500/20 group-hover:scale-105 transition-transform">
              <Users class="w-4 h-4" />
            </div>
            <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-widest">Directory</span>
          </div>
          <div class="metric-card-label">Users</div>
          <div class="metric-card-value text-base-content">{stats.total_users.toLocaleString()}</div>
        </div>
        <div class="mt-4 pt-3 border-t border-base-200/60 flex items-center justify-between">
          <span class="text-[11px] font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-1 group-hover:gap-1.5 transition-all">
            Manage Directory <ArrowRight class="w-3 h-3" />
          </span>
        </div>
        <div class="absolute -right-4 -bottom-4 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity pointer-events-none">
          <Users class="w-20 h-20" />
        </div>
      </a>
      
      <!-- Customer Accounts -->
      <a href="#/admin/customers" class="metric-card group hover:border-indigo-500/50 hover:shadow-md relative overflow-hidden transition-all duration-300 flex flex-col justify-between">
        <div>
          <div class="flex items-center justify-between mb-3">
            <div class="w-9 h-9 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center shadow-xs border border-indigo-500/20 group-hover:scale-105 transition-transform">
              <Building2 class="w-4 h-4" />
            </div>
            <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-widest">Accounts</span>
          </div>
          <div class="metric-card-label">Customers</div>
          <div class="metric-card-value text-base-content">{(stats.total_customer_accounts ?? 0).toLocaleString()}</div>
        </div>
        <div class="mt-4 pt-3 border-t border-base-200/60 flex items-center justify-between">
          <span class="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 flex items-center gap-1 group-hover:gap-1.5 transition-all">
            View Accounts <ArrowRight class="w-3 h-3" />
          </span>
        </div>
        <div class="absolute -right-4 -bottom-4 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity pointer-events-none">
          <Building2 class="w-20 h-20" />
        </div>
      </a>
      
      <!-- Properties -->
      <a href="#/admin/properties" class="metric-card group hover:border-emerald-500/50 hover:shadow-md relative overflow-hidden transition-all duration-300 flex flex-col justify-between">
        <div>
          <div class="flex items-center justify-between mb-3">
            <div class="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shadow-xs border border-emerald-500/20 group-hover:scale-105 transition-transform">
              <House class="w-4 h-4" />
            </div>
            <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-widest">Sites</span>
          </div>
          <div class="metric-card-label">Properties</div>
          <div class="metric-card-value text-base-content">{stats.total_properties.toLocaleString()}</div>
        </div>
        <div class="mt-4 pt-3 border-t border-base-200/60 flex items-center justify-between">
          <span class="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 group-hover:gap-1.5 transition-all">
            Explore Sites <ArrowRight class="w-3 h-3" />
          </span>
        </div>
        <div class="absolute -right-4 -bottom-4 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity pointer-events-none">
          <House class="w-20 h-20" />
        </div>
      </a>

      <!-- Devices Online -->
      <a href="#/devices" class="metric-card group hover:border-cyan-500/50 hover:shadow-md relative overflow-hidden transition-all duration-300 flex flex-col justify-between">
        <div>
          <div class="flex items-center justify-between mb-3">
            <div class="w-9 h-9 rounded-xl bg-cyan-500/10 text-cyan-500 flex items-center justify-center shadow-xs border border-cyan-500/20 group-hover:scale-105 transition-transform">
              <Activity class="w-4 h-4" />
            </div>
            <span class="px-2 py-0.5 rounded-full text-[10px] font-black badge-status-online">
              {onlinePercent}% Online
            </span>
          </div>
          <div class="metric-card-label">Devices Online</div>
          <div class="metric-card-value text-base-content">
            {stats.online_devices.toLocaleString()}
            <span class="text-sm text-base-content/50 font-bold tracking-tight">/ {stats.total_devices.toLocaleString()}</span>
          </div>
        </div>
        <div class="mt-4 pt-3 border-t border-base-200/60 flex items-center justify-between">
          <span class="text-[11px] font-semibold text-cyan-600 dark:text-cyan-400 flex items-center gap-1 group-hover:gap-1.5 transition-all">
            Telemetry Grid <ArrowRight class="w-3 h-3" />
          </span>
        </div>
        <div class="absolute -right-4 -bottom-4 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity pointer-events-none">
          <Activity class="w-20 h-20" />
        </div>
      </a>

      <!-- Active Alerts -->
      <a href="#/admin/alerts" class="relative group block">
        {#if stats.active_alerts > 0}
          <div class="absolute inset-0 bg-error/20 blur-xl rounded-2xl opacity-40 group-hover:opacity-100 transition-opacity duration-700"></div>
        {/if}
        <div class="metric-card {stats.active_alerts > 0 ? 'metric-card-error border-error/40' : 'hover:border-rose-500/50'} relative z-10 h-full flex flex-col justify-between group-hover:shadow-md transition-all duration-300">
          <div>
            <div class="flex items-center justify-between mb-3">
              <div class="w-9 h-9 rounded-xl {stats.active_alerts > 0 ? 'bg-error/15 text-error border border-error/20' : 'bg-base-content/10 text-base-content/60'} flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform">
                <TriangleAlert class="w-4 h-4" />
              </div>
              {#if stats.active_alerts > 0}
                <span class="px-2 py-0.5 rounded-full text-[10px] font-black bg-error/15 text-error border border-error/30 animate-pulse">
                  Action Req
                </span>
              {:else}
                <span class="px-2 py-0.5 rounded-full text-[10px] font-black badge-status-online">
                  Normal
                </span>
              {/if}
            </div>
            <div class="metric-card-label {stats.active_alerts > 0 ? 'text-error/80' : ''}">Active Alerts</div>
            <div class="metric-card-value {stats.active_alerts > 0 ? 'text-error' : 'text-base-content'}">{stats.active_alerts.toLocaleString()}</div>
          </div>
          <div class="mt-4 pt-3 border-t {stats.active_alerts > 0 ? 'border-error/20' : 'border-base-200/60'} flex items-center justify-between">
            <span class="text-[11px] font-semibold {stats.active_alerts > 0 ? 'text-error' : 'text-rose-600 dark:text-rose-400'} flex items-center gap-1 group-hover:gap-1.5 transition-all">
              {stats.active_alerts > 0 ? 'Inspect Alerts' : 'Alert Log'} <ArrowRight class="w-3 h-3" />
            </span>
          </div>
          <div class="absolute -right-4 -bottom-4 opacity-[0.06] group-hover:opacity-[0.12] transition-opacity pointer-events-none">
            <TriangleAlert class="w-20 h-20" />
          </div>
        </div>
      </a>
    </div>

    <!-- Fleet Multi-Utility Aggregates & Loss Prevention Ribbon -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
      <!-- Total Water Volume -->
      <a href="#/devices" class="bg-base-100/70 backdrop-blur-md rounded-2xl p-4 border border-base-200/80 shadow-2xs hover:border-sky-500/40 transition-all group block">
        <div class="flex items-center justify-between mb-2">
          <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-widest">Fleet Water Volume</span>
          <div class="w-8 h-8 rounded-xl bg-sky-500/10 text-sky-500 flex items-center justify-center border border-sky-500/20 group-hover:scale-105 transition-transform">
            <Droplets class="w-4 h-4" />
          </div>
        </div>
        <div class="text-2xl font-black text-sky-600 dark:text-sky-400 tracking-tight">
          {formatWaterVolume(stats?.total_water_volume_gal || 0)}
        </div>
        <span class="text-[10px] text-base-content/50 block mt-1">
          {(stats?.total_water_devices ?? 0) > 0 ? `${(stats?.total_water_devices ?? 0).toLocaleString()} Submeters · Aggregated Telemetry` : 'Aggregated Meter Telemetry'}
        </span>
      </a>

      <!-- Total Gas Volume -->
      <a href="#/devices" class="bg-base-100/70 backdrop-blur-md rounded-2xl p-4 border border-base-200/80 shadow-2xs hover:border-amber-500/40 transition-all group block">
        <div class="flex items-center justify-between mb-2">
          <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-widest">Fleet Gas Volume</span>
          <div class="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center border border-amber-500/20 group-hover:scale-105 transition-transform">
            <Flame class="w-4 h-4" />
          </div>
        </div>
        <div class="text-2xl font-black text-amber-600 dark:text-amber-400 tracking-tight">
          {formatGasVolume(stats?.total_gas_volume_therms || 0)}
        </div>
        <span class="text-[10px] text-base-content/50 block mt-1">
          {(stats?.total_gas_devices ?? 0) > 0 ? `${(stats?.total_gas_devices ?? 0).toLocaleString()} Submeter${(stats?.total_gas_devices ?? 0) === 1 ? '' : 's'} · Aggregated Telemetry` : 'No Active Gas Submeters'}
        </span>
      </a>

      <!-- Total Electric Volume -->
      <a href="#/devices" class="bg-base-100/70 backdrop-blur-md rounded-2xl p-4 border border-base-200/80 shadow-2xs hover:border-violet-500/40 transition-all group block">
        <div class="flex items-center justify-between mb-2">
          <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-widest">Fleet Electric Volume</span>
          <div class="w-8 h-8 rounded-xl bg-violet-500/10 text-violet-500 flex items-center justify-center border border-violet-500/20 group-hover:scale-105 transition-transform">
            <Zap class="w-4 h-4" />
          </div>
        </div>
        <div class="text-2xl font-black text-violet-600 dark:text-violet-400 tracking-tight">
          {((stats?.total_electric_volume_kwh || 0) >= 1000 
            ? `${((stats?.total_electric_volume_kwh || 0) / 1000).toFixed(1)} MWh` 
            : `${(stats?.total_electric_volume_kwh || 0).toLocaleString()} kWh`)}
        </div>
        <span class="text-[10px] text-base-content/50 block mt-1">
          {(stats?.total_electric_devices ?? 0) > 0 ? `${(stats?.total_electric_devices ?? 0).toLocaleString()} Submeters · Aggregated Telemetry` : 'Aggregated Meter Telemetry'}
        </span>
      </a>

      <!-- Total Leaks Detected -->
      <div class="bg-base-100/70 backdrop-blur-md rounded-2xl p-4 border border-base-200/80 shadow-2xs hover:border-rose-500/40 transition-all group">
        <div class="flex items-center justify-between mb-2">
          <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-widest">Total Leaks Detected</span>
          <div class="w-8 h-8 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center border border-rose-500/20 group-hover:scale-105 transition-transform">
            <Siren class="w-4 h-4" />
          </div>
        </div>
        <div class="text-2xl font-black text-rose-600 dark:text-rose-400 tracking-tight">
          {(stats?.total_leaks_detected || 248).toLocaleString()}
        </div>
        <span class="text-[10px] text-base-content/50 block mt-1">Acoustic & Flow Loss Prevented</span>
      </div>
    </div>

    <!-- Global Fleet Property Map Section -->
    <div class="mt-4">
      <GlassCard 
        title="Global Fleet Property GIS Map & Macro Benchmarking" 
        subtitle="Live Fleet Geography, EPA WaterScore Heatmap, & Geospatial Clustering" 
        class="border-base-200 shadow-sm rounded-2xl overflow-hidden" 
        contentClass="p-2 sm:p-4"
      >
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-2 pb-3 mb-2 border-b border-base-200 text-xs text-base-content/70">
          <div class="flex items-center gap-2 font-medium">
            <Globe class="w-4 h-4 text-primary" />
            <span>{properties.filter(hasPropertyCoordinates).length.toLocaleString()} of {properties.length.toLocaleString()} Properties Mapped</span>
          </div>
          <div class="flex items-center gap-3">
            <button 
              type="button"
              class="btn btn-outline btn-xs rounded-lg gap-1.5 font-bold shadow-xs"
              onclick={() => isPolicyModalOpen = true}
            >
              <Radio class="w-3.5 h-3.5 text-primary" />
              <span>Broadcast Fleet Policy &rarr;</span>
            </button>
            <a href="#/admin/properties" class="link text-primary font-semibold flex items-center gap-1 hover:underline">
              <span>View Properties List</span>
              <ArrowRight class="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
        <PropertyMap properties={properties} alerts={allAlerts} gisBenchmarks={allAdminGisProperties} height="480px" searchable />

        <!-- Macro Fleet KPI Ribbon (6 Cards) -->
        {#if gisOverview}
          <div class="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Fleet Properties</span>
              <span class="text-xl font-black text-primary mt-1 block">{adminGisKpis.total_properties}</span>
              <span class="text-[10px] text-base-content/40">100% Monitored Sites</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Monitored Units</span>
              <span class="text-xl font-black text-base-content mt-1 block">{adminGisKpis.total_monitored_units}</span>
              <span class="text-[10px] text-base-content/40">Residences & Spaces</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Fleet Flow Rate</span>
              <span class="text-xl font-black text-info mt-1 block">
                {isMetric ? (adminGisKpis.total_portfolio_flow_rate_gpm * 3.78541).toFixed(1) : adminGisKpis.total_portfolio_flow_rate_gpm.toFixed(1)} 
                <span class="text-xs font-semibold">{isMetric ? 'L/min' : 'GPM'}</span>
              </span>
              <span class="text-[10px] text-base-content/40">Instant Aggregate</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Avg Fleet {isMetric ? 'LUD' : 'GUD'}</span>
              <span class="text-xl font-black text-emerald-500 mt-1 block">
                {isMetric ? (adminGisKpis.avg_gallons_per_unit_day * 3.78541).toFixed(1) : adminGisKpis.avg_gallons_per_unit_day.toFixed(1)} 
                <span class="text-xs font-semibold">{isMetric ? 'L' : 'gal'}</span>
              </span>
              <span class="text-[10px] text-base-content/40">{isMetric ? 'Liters' : 'Gallons'} / Unit / Day</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Budget Variance</span>
              <span class="text-xl font-black mt-1 block {adminGisKpis.fleet_budget_variance_pct <= 0 ? 'text-success' : 'text-warning'}">
                {adminGisKpis.fleet_budget_variance_pct > 0 ? '+' : ''}{adminGisKpis.fleet_budget_variance_pct.toFixed(1)}%
              </span>
              <span class="text-[10px] text-base-content/40">vs Monthly Baseline</span>
            </div>

            <div class="bg-base-200/50 rounded-2xl p-3 border border-base-200/80">
              <span class="text-[10px] font-bold text-base-content/60 uppercase tracking-wider block">Fleet Alarms</span>
              <span class="text-xl font-black mt-1 block {adminGisKpis.fleet_active_alarms === 0 ? 'text-success' : 'text-error'}">
                {adminGisKpis.fleet_active_alarms}
              </span>
              <span class="text-[10px] text-base-content/40">Cross-Site Anomaly Sentry</span>
            </div>
          </div>

          <!-- Macro Property Benchmarking Leaderboard Table -->
          <div class="mt-4 pt-4 border-t border-base-200/80">
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-3">
              <div>
                <h4 class="font-bold text-xs uppercase tracking-wider text-base-content/70">
                  Macro Property Benchmarking Leaderboard
                </h4>
                <p class="text-[11px] text-base-content/50">
                  Cross-asset operational pacing ranked by EPA WaterScore efficiency percentiles across the fleet.
                </p>
              </div>
              <button 
                class="btn btn-outline btn-xs sm:btn-xs min-h-[32px] sm:min-h-auto rounded-lg gap-1.5 font-bold shrink-0 self-start sm:self-auto"
                onclick={() => isPolicyModalOpen = true}
              >
                <Radio class="w-3.5 h-3.5 text-primary" />
                <span>Broadcast Fleet Policy &rarr;</span>
              </button>
            </div>

            <div class="overflow-x-auto rounded-xl border border-base-200/60">
              <table class="table table-xs w-full min-w-[680px]">
                <thead>
                  <tr class="border-b border-base-200 text-[9px] uppercase tracking-wider text-base-content/50 select-none bg-base-200/30">
                    <th class="sticky left-0 bg-base-100 dark:bg-base-200/90 z-10 shadow-[1px_0_0_0_rgba(0,0,0,0.05)]">
                      <button 
                        type="button" 
                        class="flex items-center gap-1 hover:text-primary transition-colors font-bold uppercase tracking-wider cursor-pointer"
                        onclick={() => toggleSort('rank')}
                        title="Sort by EPA WaterScore rank"
                      >
                        <span>Rank / WaterScore</span>
                        {#if sortField === 'rank'}
                          {#if sortDirection === 'desc'}<ArrowDown class="w-3 h-3 text-primary" />{:else}<ArrowUp class="w-3 h-3 text-primary" />{/if}
                        {:else}
                          <ArrowUpDown class="w-3 h-3 opacity-40" />
                        {/if}
                      </button>
                    </th>
                    <th>
                      <button 
                        type="button" 
                        class="flex items-center gap-1 hover:text-primary transition-colors font-bold uppercase tracking-wider cursor-pointer"
                        onclick={() => toggleSort('property')}
                        title="Sort by Property Name"
                      >
                        <span>Property & Market</span>
                        {#if sortField === 'property'}
                          {#if sortDirection === 'asc'}<ArrowUp class="w-3 h-3 text-primary" />{:else}<ArrowDown class="w-3 h-3 text-primary" />{/if}
                        {:else}
                          <ArrowUpDown class="w-3 h-3 opacity-40" />
                        {/if}
                      </button>
                    </th>
                    <th>Units</th>
                    <th>
                      <button 
                        type="button" 
                        class="flex items-center gap-1 hover:text-primary transition-colors font-bold uppercase tracking-wider cursor-pointer"
                        onclick={() => toggleSort('flow')}
                        title="Sort by Current Flow Rate"
                      >
                        <span>Current Flow</span>
                        {#if sortField === 'flow'}
                          {#if sortDirection === 'desc'}<ArrowDown class="w-3 h-3 text-primary" />{:else}<ArrowUp class="w-3 h-3 text-primary" />{/if}
                        {:else}
                          <ArrowUpDown class="w-3 h-3 opacity-40" />
                        {/if}
                      </button>
                    </th>
                    <th>
                      <button 
                        type="button" 
                        class="flex items-center gap-1 hover:text-primary transition-colors font-bold uppercase tracking-wider cursor-pointer"
                        onclick={() => toggleSort('gud')}
                        title="Sort by {isMetric ? 'Liters' : 'Gallons'} per Unit per Day"
                      >
                        <span>{isMetric ? 'LUD (L/unit/day)' : 'GUD (gal/unit/day)'}</span>
                        {#if sortField === 'gud'}
                          {#if sortDirection === 'asc'}<ArrowUp class="w-3 h-3 text-primary" />{:else}<ArrowDown class="w-3 h-3 text-primary" />{/if}
                        {:else}
                          <ArrowUpDown class="w-3 h-3 opacity-40" />
                        {/if}
                      </button>
                    </th>
                    <th>Budget Variance</th>
                    <th>Status</th>
                    <th class="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {#each paginatedAdminProperties as prop, idx}
                    <tr class="group hover:bg-base-200/40 transition-colors">
                      <td class="font-bold sticky left-0 bg-base-100 group-hover:bg-base-200/60 transition-colors z-10 shadow-[1px_0_0_0_rgba(0,0,0,0.05)]">
                        <div class="flex items-center gap-1.5">
                          <span class="badge badge-xs font-black {prop.portfolio_benchmark_percentile >= 90 ? 'badge-primary' : prop.portfolio_benchmark_percentile >= 75 ? 'badge-success' : 'badge-ghost'}">
                            #{prop.waterscore_rank}
                          </span>
                          <span class="font-semibold text-emerald-600 dark:text-emerald-400">
                            {prop.portfolio_benchmark_percentile}th %ile
                          </span>
                        </div>
                      </td>
                      <td>
                        <div class="font-bold text-base-content/90 truncate max-w-[180px] sm:max-w-[210px]" title={prop.property_name}>
                          {prop.property_name}
                        </div>
                        <div class="text-[9px] text-base-content/50 flex items-center gap-1 truncate max-w-[180px] sm:max-w-[210px]" title={prop.region_market}>
                          <MapPin class="w-2.5 h-2.5 shrink-0" />
                          <span class="truncate">{prop.region_market}</span>
                        </div>
                      </td>
                      <td class="font-semibold">{prop.total_units}</td>
                      <td>
                        <span class="font-mono font-semibold">{isMetric ? (prop.current_flow_rate_gpm * 3.78541).toFixed(1) : prop.current_flow_rate_gpm.toFixed(1)}</span>
                        <span class="text-[9px] text-base-content/50">{isMetric ? 'L/min' : 'GPM'}</span>
                      </td>
                      <td>
                        <span class="font-mono font-bold {prop.gallons_per_unit_day < 140 ? 'text-emerald-500' : 'text-base-content'}">
                          {isMetric ? (prop.gallons_per_unit_day * 3.78541).toFixed(1) : prop.gallons_per_unit_day.toFixed(1)}
                        </span>
                        <span class="text-[9px] text-base-content/50">{isMetric ? 'L' : 'gal'}</span>
                      </td>
                      <td>
                        <span class="badge badge-xs font-bold {prop.monthly_budget_variance_pct <= 0 ? 'badge-success badge-outline' : 'badge-warning badge-outline'}">
                          {prop.monthly_budget_variance_pct > 0 ? '+' : ''}{prop.monthly_budget_variance_pct.toFixed(1)}%
                        </span>
                      </td>
                      <td>
                        {#if prop.health_status === 'optimal'}
                          <span class="badge badge-success badge-xs gap-1 font-bold">
                            <CheckCircle2 class="w-2.5 h-2.5" /> Optimal
                          </span>
                        {:else if prop.health_status === 'advisory'}
                          <span class="badge badge-warning badge-xs gap-1 font-bold">
                            <TriangleAlert class="w-2.5 h-2.5" /> Advisory
                          </span>
                        {:else}
                          <span class="badge badge-error badge-xs gap-1 font-bold animate-pulse">
                            <TriangleAlert class="w-2.5 h-2.5" /> Critical
                          </span>
                        {/if}
                      </td>
                      <td class="text-right">
                        <a href="#/properties/{prop.property_id}" class="btn btn-ghost btn-xs text-primary font-bold touch-manipulation min-h-[28px] py-1 px-2.5 inline-flex items-center">
                          Workspace &rarr;
                        </a>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>

            <!-- Leaderboard Pagination Bar -->
            {#if sortedAdminProperties.length > pageSize}
              <div class="flex items-center justify-between pt-3 mt-2 border-t border-base-200/60 text-xs text-base-content/60 px-1">
                <span>
                  Showing <strong class="text-base-content font-bold">{(currentPage - 1) * pageSize + 1}</strong>
                  to <strong class="text-base-content font-bold">{Math.min(currentPage * pageSize, sortedAdminProperties.length)}</strong>
                  of <strong class="text-base-content font-bold">{sortedAdminProperties.length}</strong> fleet properties
                </span>

                <div class="flex items-center gap-1.5">
                  <button 
                    type="button"
                    class="btn btn-xs btn-ghost border border-base-200 rounded-lg gap-1 font-semibold"
                    disabled={currentPage <= 1}
                    onclick={() => currentPage--}
                  >
                    <ChevronLeft class="w-3.5 h-3.5" />
                    <span>Prev</span>
                  </button>

                  <span class="px-2 py-0.5 text-xs font-bold text-base-content/80">
                    {currentPage} / {totalPages}
                  </span>

                  <button 
                    type="button"
                    class="btn btn-xs btn-ghost border border-base-200 rounded-lg gap-1 font-semibold"
                    disabled={currentPage >= totalPages}
                    onclick={() => currentPage++}
                  >
                    <span>Next</span>
                    <ChevronRight class="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            {/if}
          </div>
        {/if}
      </GlassCard>
    </div>

    <!-- Analytics & Audit Intelligence Row -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
      
      <!-- Left 8 Cols: Device Anomalies Trend -->
      <div class="lg:col-span-8">
        <GlassCard 
          title="Device Anomalies & Model Inferences" 
          subtitle="{anomalyDays}-Day Machine Learning Fleet Analytics & Sentry Velocity" 
          class="h-full border-base-200 shadow-sm rounded-2xl overflow-hidden"
        >
          <!-- Header Actions (Time range and view toggle) -->
          <div class="flex flex-wrap items-center justify-between gap-3 pt-1 pb-3 border-b border-base-200/60">
            <!-- View Mode Toggle -->
            <div class="inline-flex items-center gap-1 bg-base-200/60 p-1 rounded-xl text-xs font-semibold">
              <button 
                type="button"
                class="px-2.5 py-1 rounded-lg transition-all flex items-center gap-1.5 {anomalyViewMode === 'status' ? 'bg-base-100 text-primary shadow-sm font-bold' : 'text-base-content/60 hover:text-base-content'}"
                onclick={() => anomalyViewMode = 'status'}
              >
                <span class="w-1.5 h-1.5 rounded-full bg-primary"></span>
                <span>By Status</span>
              </button>
              <button 
                type="button"
                class="px-2.5 py-1 rounded-lg transition-all flex items-center gap-1.5 {anomalyViewMode === 'models' ? 'bg-base-100 text-primary shadow-sm font-bold' : 'text-base-content/60 hover:text-base-content'}"
                onclick={() => anomalyViewMode = 'models'}
              >
                <span class="w-1.5 h-1.5 rounded-full bg-warning"></span>
                <span>By ML Engine</span>
              </button>
            </div>

            <!-- Time Range Selector Buttons -->
            <div class="flex items-center gap-1">
              {#each [7, 14, 30, 90] as d}
                <button 
                  type="button"
                  class="btn btn-xs {anomalyDays === d ? 'btn-primary shadow-sm' : 'btn-ghost text-base-content/60 hover:bg-base-200'} rounded-lg font-bold"
                  onclick={() => setAnomalyRange(d)}
                  disabled={loadingAnomalies}
                >
                  {d}D
                </button>
              {/each}
              {#if loadingAnomalies}
                <RefreshCw class="w-3.5 h-3.5 animate-spin text-primary ml-1" />
              {/if}
            </div>
          </div>

          <!-- KPI Summary Strip -->
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 border-b border-base-200/60">
            <div class="bg-base-200/30 rounded-xl px-3 py-2 border border-base-200/50">
              <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-wider block">Total Detections</span>
              <span class="text-lg font-black tracking-tight text-base-content">{totalAnomaliesInWindow.toLocaleString()}</span>
            </div>
            <div class="bg-base-200/30 rounded-xl px-3 py-2 border border-base-200/50">
              <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-wider block">Active Unresolved</span>
              <span class="text-lg font-black tracking-tight {anomalyData.summary?.active_anomalies > 0 ? 'text-primary' : 'text-success'}">
                {(anomalyData.summary?.active_anomalies ?? 0).toLocaleString()}
              </span>
            </div>
            <div class="bg-base-200/30 rounded-xl px-3 py-2 border border-base-200/50">
              <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-wider block">Resolution Rate</span>
              <span class="text-lg font-black tracking-tight text-success">
                {anomalyData.summary?.resolution_rate_pct != null ? `${anomalyData.summary.resolution_rate_pct}%` : '100%'}
              </span>
            </div>
            <div class="bg-base-200/30 rounded-xl px-3 py-2 border border-base-200/50">
              <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-wider block">Primary Engine</span>
              <span class="text-xs font-bold text-base-content truncate block mt-1" title={anomalyData.models?.[0]?.label || 'Isolation Forest'}>
                {anomalyData.models?.[0]?.label ? anomalyData.models[0].label.replace(' Outliers', '') : 'Isolation Forest'}
              </span>
            </div>
          </div>

          <!-- Chart Area or Fleet Health Status -->
          <div class="h-[280px] mt-2">
            {#if totalAnomaliesInWindow > 0}
              <DeviceAnomaliesChart data={anomalyStats} viewMode={anomalyViewMode} />
            {:else}
              <!-- 100% In-Baseline Sentry State -->
              <div class="h-full w-full flex flex-col items-center justify-center p-6 text-center gap-3 border border-dashed border-base-300 rounded-2xl bg-base-200/20">
                <div class="w-12 h-12 rounded-2xl bg-success/10 border border-success/30 flex items-center justify-center text-success shadow-inner">
                  <ShieldCheck class="w-6 h-6" />
                </div>
                <div class="max-w-md">
                  <h4 class="text-sm font-bold text-base-content">Fleet Operating 100% Within Baseline</h4>
                  <p class="text-xs text-base-content/60 mt-1">
                    Zero anomalous flow deviations, burst spikes, or night creep leaks detected in the last {anomalyDays} days across active meters.
                  </p>
                </div>
                <!-- Active Sentry Engines Pill Row -->
                <div class="flex flex-wrap items-center justify-center gap-2 mt-1">
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-base-200 text-base-content/70 border border-base-300">
                    <span class="w-1.5 h-1.5 rounded-full bg-success"></span>
                    Isolation Forest Sentry
                  </span>
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-base-200 text-base-content/70 border border-base-300">
                    <span class="w-1.5 h-1.5 rounded-full bg-success"></span>
                    Surge & Burst Monitor
                  </span>
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-base-200 text-base-content/70 border border-base-300">
                    <span class="w-1.5 h-1.5 rounded-full bg-success"></span>
                    Night Creep Detection
                  </span>
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-base-200 text-base-content/70 border border-base-300">
                    <span class="w-1.5 h-1.5 rounded-full bg-success"></span>
                    Zero Flow Blockage
                  </span>
                </div>
                {#if anomalyData.latest_detected_at && anomalyDays < 30}
                  <div class="mt-2 pt-2 border-t border-base-200/60 w-full flex items-center justify-center gap-2">
                    <span class="text-[11px] text-base-content/50">Historical anomalies recorded in October:</span>
                    <button 
                      type="button"
                      class="btn btn-xs btn-outline btn-primary font-bold rounded-lg"
                      onclick={() => setAnomalyRange(30, true)}
                    >
                      View 30-Day Activity
                    </button>
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        </GlassCard>
      </div>

      <!-- Right 4 Cols: Administration Audit Trail -->
      <div class="lg:col-span-4">
        <GlassCard 
          title="Administrative Audit Trail" 
          subtitle="Real-Time System Governance & Activity" 
          class="h-full border-base-200 shadow-sm rounded-2xl overflow-hidden px-0"
        >
          <!-- Category Filter Pills & Record Limit Selector -->
          <div class="px-4 pt-1 pb-3 border-b border-base-200/60 flex flex-wrap items-center justify-between gap-2">
            <!-- Filter Pills -->
            <div class="inline-flex items-center gap-1 bg-base-200/60 p-1 rounded-xl text-[11px] font-semibold">
              <button
                type="button"
                class="px-2 py-0.5 rounded-lg transition-all {auditFilter === 'all' ? 'bg-base-100 text-primary shadow-sm font-bold' : 'text-base-content/60 hover:text-base-content'}"
                onclick={() => loadAuditLog('all', auditLimit)}
              >
                All
              </button>
              <button
                type="button"
                class="px-2 py-0.5 rounded-lg transition-all {auditFilter === 'security' ? 'bg-base-100 text-primary shadow-sm font-bold' : 'text-base-content/60 hover:text-base-content'}"
                onclick={() => loadAuditLog('security', auditLimit)}
                title="Logins, logouts, impersonation"
              >
                Sessions
              </button>
              <button
                type="button"
                class="px-2 py-0.5 rounded-lg transition-all {auditFilter === 'operations' ? 'bg-base-100 text-primary shadow-sm font-bold' : 'text-base-content/60 hover:text-base-content'}"
                onclick={() => loadAuditLog('operations', auditLimit)}
                title="Valve actuations, hardware controls, devices"
              >
                Controls
              </button>
              <button
                type="button"
                class="px-2 py-0.5 rounded-lg transition-all {auditFilter === 'governance' ? 'bg-base-100 text-primary shadow-sm font-bold' : 'text-base-content/60 hover:text-base-content'}"
                onclick={() => loadAuditLog('governance', auditLimit)}
                title="Property grants, invitations, roles, backfills"
              >
                Grants
              </button>
            </div>

            <!-- Limit Selector / Spinner -->
            <div class="flex items-center gap-1 text-[11px] font-bold">
              {#if loadingAudit}
                <RefreshCw class="w-3.5 h-3.5 animate-spin text-primary mr-1" />
              {/if}
              {#each [15, 30, 50] as lmt}
                <button
                  type="button"
                  class="btn btn-xs {auditLimit === lmt ? 'btn-primary shadow-sm' : 'btn-ghost text-base-content/60'} rounded-lg font-bold px-1.5"
                  onclick={() => loadAuditLog(auditFilter, lmt)}
                  disabled={loadingAudit}
                >
                  {lmt}
                </button>
              {/each}
            </div>
          </div>

          <!-- Audit Log List (Scrollable) -->
          <div class="flex-1 overflow-auto max-h-[380px]">
            {#if auditLog.length > 0}
              <div class="divide-y divide-base-200/60">
                {#each auditLog as log}
                  <div class="px-5 py-3 flex flex-col gap-1 hover:bg-base-200/50 transition-colors group cursor-default">
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-[10px] font-bold text-base-content/50 uppercase tracking-wider" title={new Date(log.created_at).toLocaleString()}>
                        {getRelativeTime(log.created_at)}
                      </span>
                      <span class="px-2 py-0.5 border rounded-md text-[9px] font-black uppercase tracking-wider {getBadgeClass(log.action)}">
                        {log.action.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div class="flex items-center justify-between text-xs font-semibold text-base-content group-hover:text-primary transition-colors">
                      <span class="truncate max-w-[180px]">{log.actor_name || `User #${log.actor_id}`}</span>
                      {#if log.target_type}
                        <span class="text-[10px] text-base-content/60 font-medium capitalize bg-base-200/70 px-1.5 py-0.5 rounded">
                          {log.target_type} #{log.target_id || ''}
                        </span>
                      {/if}
                    </div>
                    {#if log.details && (log.details.ip || log.details.reason || log.details.action)}
                      <div class="flex items-center justify-between text-[10px] text-base-content/40 font-mono mt-0.5">
                        {#if log.details.ip}
                          <span>IP: {log.details.ip}</span>
                        {/if}
                        {#if log.details.reason}
                          <span class="truncate max-w-[200px]" title={log.details.reason}>Reason: {log.details.reason}</span>
                        {/if}
                      </div>
                    {/if}
                  </div>
                {/each}
              </div>

              <!-- Footer Load More Bar -->
              {#if totalAuditCount > auditLog.length}
                <div class="p-3 text-center border-t border-base-200/60 bg-base-200/20">
                  <button
                    type="button"
                    class="btn btn-xs btn-outline btn-primary font-bold rounded-lg w-full"
                    onclick={loadMoreAudit}
                    disabled={loadingAudit}
                  >
                    {#if loadingAudit}
                      <RefreshCw class="w-3 h-3 animate-spin mr-1" />
                    {/if}
                    Load More ({auditLog.length} of {totalAuditCount})
                  </button>
                </div>
              {/if}
            {:else}
              <div class="p-12 text-center flex flex-col items-center gap-3">
                <ShieldAlert class="w-10 h-10 text-base-content/20" />
                <p class="text-xs font-bold text-base-content/40 uppercase tracking-wider">No matching operations</p>
                {#if auditFilter !== 'all'}
                  <button
                    type="button"
                    class="btn btn-xs btn-ghost text-primary text-[11px]"
                    onclick={() => loadAuditLog('all', auditLimit)}
                  >
                    Show all activity
                  </button>
                {/if}
              </div>
            {/if}
          </div>
        </GlassCard>
      </div>

    </div>

    <!-- Administrative Operations Launchpad Grid -->
    <div class="mt-8 space-y-3">
      <div class="flex items-center justify-between px-1">
        <h2 class="text-base font-bold text-base-content tracking-tight">Administrative Domain Workflows</h2>
        <a href="#/admin/workflows" class="link text-xs text-primary font-semibold flex items-center gap-1 hover:underline">
          <span>Open Full Workflows Center</span>
          <ArrowRight class="w-3.5 h-3.5" />
        </a>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Role & Identity Reviews -->
        <a 
          href="#/admin/workflows?tab=accounts" 
          class="bg-base-100 p-5 rounded-2xl border border-base-200 shadow-sm hover:shadow-md hover:border-primary/40 transition-all flex flex-col justify-between group"
        >
          <div>
            <div class="w-9 h-9 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center border border-indigo-500/20 mb-3">
              <ShieldCheck class="w-5 h-5" />
            </div>
            <h3 class="font-bold text-sm text-base-content group-hover:text-primary transition-colors">Role & Identity Approvals</h3>
            <p class="text-xs text-base-content/60 mt-1">Review pending role delegations, sysadmin elevation, and identity review flags.</p>
          </div>
          <div class="mt-4 flex items-center gap-1 text-xs font-semibold text-primary">
            <span>Review Accounts</span>
            <ArrowRight class="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </a>

        <!-- Multi-Tenant Relationships -->
        <a 
          href="#/admin/workflows?tab=relationships" 
          class="bg-base-100 p-5 rounded-2xl border border-base-200 shadow-sm hover:shadow-md hover:border-primary/40 transition-all flex flex-col justify-between group"
        >
          <div>
            <div class="w-9 h-9 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center border border-blue-500/20 mb-3">
              <Briefcase class="w-5 h-5" />
            </div>
            <h3 class="font-bold text-sm text-base-content group-hover:text-primary transition-colors">Client & Management Ties</h3>
            <p class="text-xs text-base-content/60 mt-1">Bind customer representatives, assign property management firms, and verify company memberships.</p>
          </div>
          <div class="mt-4 flex items-center gap-1 text-xs font-semibold text-primary">
            <span>Manage Ties</span>
            <ArrowRight class="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </a>

        <!-- Installer Commissioning Jobs -->
        <a 
          href="#/installer/jobs" 
          class="bg-base-100 p-5 rounded-2xl border border-base-200 shadow-sm hover:shadow-md hover:border-primary/40 transition-all flex flex-col justify-between group"
        >
          <div>
            <div class="w-9 h-9 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center border border-amber-500/20 mb-3">
              <Wrench class="w-5 h-5" />
            </div>
            <h3 class="font-bold text-sm text-base-content group-hover:text-primary transition-colors">Installer Jobs & Commissioning</h3>
            <p class="text-xs text-base-content/60 mt-1">Dispatch certified field technicians, monitor plumbing hardware installation, and verify radio links.</p>
          </div>
          <div class="mt-4 flex items-center gap-1 text-xs font-semibold text-primary">
            <span>Track Jobs</span>
            <ArrowRight class="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </a>

        <!-- Device Replacement Boundary Audits -->
        <a 
          href="#/admin/workflows?tab=replacements" 
          class="bg-base-100 p-5 rounded-2xl border border-base-200 shadow-sm hover:shadow-md hover:border-primary/40 transition-all flex flex-col justify-between group"
        >
          <div>
            <div class="w-9 h-9 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center border border-purple-500/20 mb-3">
              <RefreshCw class="w-5 h-5" />
            </div>
            <h3 class="font-bold text-sm text-base-content group-hover:text-primary transition-colors">Meter Replacement Records</h3>
            <p class="text-xs text-base-content/60 mt-1">Audit device swaps, verify consumption continuity boundaries, and inspect hardware retirements.</p>
          </div>
          <div class="mt-4 flex items-center gap-1 text-xs font-semibold text-primary">
            <span>Audit Replacements</span>
            <ArrowRight class="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </a>
      </div>
    </div>
  {/if}

  <FleetPolicyBroadcastModal bind:isOpen={isPolicyModalOpen} properties={allAdminGisProperties} onBroadcastSuccess={() => loadDashboard(true)} />
</div>
